<template>
  <div class="tags-management">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>标签管理</span>
          <el-button type="primary" size="small" @click="handleAddTag">添加标签</el-button>
        </div>
      </template>

      <el-table :data="tags" style="width: 100%" row-key="id" :tree-props="{ children: 'children' }" :indent="20">
        <el-table-column prop="tag_name" label="标签名称" width="180" />
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="tag_code" label="标签编码" width="180" />
        <el-table-column prop="description" label="标签描述" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button-group>
              <el-button size="small" @click="handleEditTag(row)">编辑</el-button>
              <el-button size="small" type="danger" @click="handleDeleteTag(row.id)">删除</el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑标签对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="500px">
      <el-form :model="tagForm" ref="tagFormRef" :rules="rules" label-width="100px">
        <el-form-item label="父节点标签" prop="parent_id">
          <el-tree-select
            v-model="tagForm.parent_id"
            :data="parentTags"
            :props="{ label: 'tag_name', value: 'id', children: 'children' }"
            placeholder="请选择父节点标签"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="标签名称" prop="name">
          <el-input v-model="tagForm.tag_name" placeholder="请输入标签名称" />
        </el-form-item>
        <el-form-item label="标签编码" prop="name">
          <el-input v-model="tagForm.tag_code" placeholder="请输入标签编码" />
        </el-form-item>
        <el-form-item label="标签描述" prop="description">
          <el-input v-model="tagForm.description" type="textarea" placeholder="请输入标签描述" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getTags, createTag, updateTag, deleteTag, getAllTags } from '@/api/tags'

// 表格数据
const tags = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(50)

// 父节点标签数据
const parentTags = ref([])

// 对话框数据
const dialogVisible = ref(false)
const dialogTitle = ref('添加标签')
const tagForm = reactive({
  id: '',
  tag_name: '',
  tag_code: '',
  description: '',
  parent_id: ''
})
const tagFormRef = ref()

// 表单验证规则
const rules = {
  tag_name: [
    { required: true, message: '请输入标签名称', trigger: 'blur' },
    { min: 1, max: 20, message: '标签名称长度在1-20个字符之间', trigger: 'blur' }
  ],
  tag_code: [
    { required: true, message: '请输入标签编码', trigger: 'blur' },
    { min: 1, max: 20, message: '标签编码长度在1-20个字符之间', trigger: 'blur' }
  ],
  description: [
    { max: 200, message: '标签描述不能超过200个字符', trigger: 'blur' }
  ]
}

// 加载父节点标签数据
const loadParentTags = async () => {
  try {
    const response = await getAllTags()
    // 假设后端返回的是扁平结构，需要转换成树形结构
    // 这里简化处理，实际项目中可能需要根据具体数据结构进行转换
    parentTags.value = response.data.data || []
  } catch (error) {
    ElMessage.error('获取父节点标签失败')
    console.error('获取父节点标签失败:', error)
  }
}

// 加载标签数据
const loadTags = async () => {
  try {
    const response = await getTags({})
    tags.value = response.data.data || []
    total.value = response.data.total || 0
  } catch (error) {
    ElMessage.error('获取标签列表失败')
    console.error('获取标签列表失败:', error)
  }
}

// 添加标签9kk
const handleAddTag = () => {
  dialogTitle.value = '添加标签'
  tagForm.id = ''
  tagForm.tag_name = ''
  tagForm.tag_code = ''
  tagForm.description = ''
  tagForm.parent_id = ''
  dialogVisible.value = true
}

// 编辑标签
const handleEditTag = (row) => {
  dialogTitle.value = '编辑标签'
  tagForm.id = row.id
  tagForm.tag_name = row.tag_name
  tagForm.tag_code = row.tag_code
  tagForm.description = row.description
  tagForm.parent_id = row.parent_id || ''
  dialogVisible.value = true
}

// 提交表单
const handleSubmit = async () => {
  try {
    await tagFormRef.value.validate()
    if (tagForm.id) {
        // 编辑标签
        await updateTag(tagForm.id, {
          tag_name: tagForm.tag_name,
          tag_code: tagForm.tag_code,
          description: tagForm.description,
          parent_id: tagForm.parent_id || 0
        })
        ElMessage.success('标签更新成功')
      } else {
        // 添加标签
        await createTag({
          tag_name: tagForm.tag_name,
          tag_code: tagForm.tag_code,
          description: tagForm.description,
          parent_id: tagForm.parent_id || 0
        })
        ElMessage.success('标签添加成功')
      }
    dialogVisible.value = false
    loadTags()
  } catch (error) {
    if (error.name === 'ValidationError') {
      return
    }
    ElMessage.error('操作失败，请重试')
    console.error('提交标签表单失败:', error)
  }
}

// 删除标签
const handleDeleteTag = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除该标签吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deleteTag(id)
    ElMessage.success('标签删除成功')
    loadTags()
  } catch (error) {
    if (error === 'cancel') {
      return
    }
    ElMessage.error('删除标签失败')
    console.error('删除标签失败:', error)
  }
}

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size
  loadTags()
}

const handleCurrentChange = (current) => {
  currentPage.value = current
  loadTags()
}

// 初始加载
onMounted(() => {
  console.log('Tags组件已挂载');
  loadTags()
  loadParentTags()
})
</script>

<style scoped>
.tags-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>